<template>
  <div v-if="layout === 'vertical'" class="rank-card" @click="$emit('click')">
    <img :src="book.coverImage" :alt="book.title" class="rank-card__cover">

    <div class="rank-card__overlay">
      <div class="rank-card__title">{{ book.title }}</div>
      <div class="rank-card__author">{{ book.author }}</div>
    </div>

    <div class="rank-card__tags">
      <div class="rank-card__tag rank-card__tag--category">{{ book.category }}</div>
      <div class="rank-card__tag rank-card__tag--rating">
        <img src="@/assets/star.svg" alt="star" class="rank-card__tag-icon">
        <span>{{ book.rating }}</span>
      </div>
    </div>
  </div>
  <div v-else-if="layout === 'horizontal'" class="rank-card-horizontal" :style="horizontalCardStyle"
    @click="$emit('click')">
    <div class="rank-card-horizontal__overlay-gradient"></div>
    <div class="rank-card-horizontal__content">
      <img :src="book.coverImage" :alt="book.title" class="rank-card-horizontal__cover">
      <div class="rank-card-horizontal__info">
        <div class="rank-card-horizontal__info-top">
          <div class="rank-card-horizontal__title">{{ book.title }}</div>
          <div class="rank-card-horizontal__author">By {{ book.author }}</div>
        </div>
        <div class="rank-card-horizontal__info-bottom">
          <div class="rank-card-horizontal__tag">{{ book.category }}</div>
          <div class="rank-card-horizontal__tag rank-card-horizontal__tag--rating">
            <img src="@/assets/star.svg" alt="star">
            <span>{{ book.rating }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-else-if="layout === 'cover-hover'" class="rank-card-cover-hover" @click="$emit('click')">
    <img :src="book.coverImage" :alt="book.title" class="rank-card-cover-hover__cover" />
    <div v-if="book.rank && book.rank <= 3" class="rank-card-cover-hover__rank-badge">Top
      {{ book.rank }}</div>
    <div class="rank-card-cover-hover__overlay">
      <div class="rank-card-cover-hover__overlay-content">
        <div class="rank-card-cover-hover__info">
          <div class="rank-card-cover-hover__title">{{ book.title }}</div>
          <div class="rank-card-cover-hover__author">By {{ book.author }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BookCard',
  props: {
    book: {
      type: Object,
      required: true,
    },
    layout: {
      type: String,
      default: 'vertical', // 'vertical' or 'horizontal'
    },
  },
  computed: {
    horizontalCardStyle () {
      if (this.layout === 'horizontal' && this.book.coverImage) {
        return {
          backgroundImage: `url(${this.book.coverImage})`,
        };
      }
      return {};
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/css/variables.scss';

.rank-card {
	width: rem(252);
	height: rem(378);
	border-radius: rem(12);
	overflow: hidden;
	position: relative;
	cursor: pointer;
	box-shadow: 0px 4px 8px 0px rgba(22, 19, 80, 0.28);
	transition: transform 0.3s ease-in-out;

	&:hover {
		transform: translateY(-4px);
	}

	&__cover {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	&__overlay {
		position: absolute;
		bottom: 0;
		left: 0;
		right: 0;
		padding: rem(24) rem(12) rem(12);
		background: linear-gradient(180deg, rgba(22, 19, 80, 0) 0%, rgba(22, 19, 80, 0.5) 20%, rgba(22, 19, 80, 1) 100%);
		color: #fff;
		display: flex;
		flex-direction: column;
		gap: rem(8);
	}

	&__title {
		font-size: rem(20);
		font-weight: 900;
		line-height: 1.2;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	&__author {
		font-size: rem(12);
		font-weight: 400;
		color: #848c8e;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	&__tags {
		position: absolute;
		top: 0;
		left: 0;
		display: flex;
		flex-direction: column;
		align-items: flex-start;
	}

	&__tag {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: rem(8);
		font-size: rem(13);
		font-weight: 700;
		color: #fff;
		border: 1px solid rgba(255, 255, 255, 0.1);

		&--category {
			background: #161350;
			padding: rem(6) rem(12);
			border-radius: rem(12) 0 rem(12) 0;
		}

		&--rating {
			background: #7f0026;
			padding: rem(6) rem(12);
			width: rem(64);
			height: rem(24);
			border-radius: 0 0 rem(12) 0;
			box-sizing: border-box;

			.rank-card__tag-icon {
				width: rem(12);
				height: rem(12);
			}
		}
	}
}

// Styles for the new horizontal layout
.rank-card-horizontal {
	position: relative;
	width: rem(344);
	height: rem(280);
	border-radius: rem(12);
	overflow: hidden;
	cursor: pointer;
	transition: transform 0.3s ease;
	background-size: cover;
	background-position: center;

	&:hover {
		transform: translateY(-4px);
	}

	&__overlay-gradient {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background: linear-gradient(180deg, rgba(22, 19, 80, 0.9) 0%, rgba(22, 19, 80, 0.3) 100%);
		backdrop-filter: blur(6px);
		z-index: 1;
	}

	&__content {
		position: relative;
		z-index: 2;
		display: flex;
		align-items: center;
		height: 100%;
		padding: rem(20);
		box-sizing: border-box;
	}

	&__cover {
		width: rem(160);
		height: rem(240);
		border-radius: rem(12);
		object-fit: cover;
		box-shadow: 4px 4px 8px 0px rgba(22, 19, 80, 0.28);
		border: 1px solid rgba(255, 255, 255, 0.2);
		flex-shrink: 0;
	}

	&__info {
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		height: rem(240);
		padding-left: rem(16);
		flex: 1;
		color: #fff;
		overflow: hidden;
	}

	&__info-top {
		display: flex;
		flex-direction: column;
		gap: rem(16);
	}

	&__title {
		font-size: rem(20);
		font-weight: 900;
		line-height: 1.2;
		max-height: calc(1.2em * 4); // Fallback for max-height
		display: -webkit-box;
		-webkit-line-clamp: 4;
		-webkit-box-orient: vertical;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	&__author {
		font-size: rem(12);
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	&__info-bottom {
		display: flex;
		flex-direction: column;
		gap: rem(8);
		align-items: flex-start;
	}

	&__tag {
		display: inline-flex;
		align-items: center;
		gap: rem(8);
		padding: rem(6) rem(12);
		border-radius: rem(8);
		font-size: rem(13);
		font-weight: 700;
		background: #161350;
		border: 1px solid rgba(255, 255, 255, 0.1);

		&--rating {
			background: #7f0026;
			img {
				width: rem(12);
				height: rem(12);
			}
		}
	}
}

.rank-card-cover-hover {
	position: relative;
	// width: rem(160);
	flex: 0 0 calc((100% - rem(126)) / 10); /* 使每个子元素占1/3的宽度 */
	height: rem(144);
	border-radius: rem(12);
	overflow: hidden;
	cursor: pointer;
	transition: transform 0.3s ease;
	box-shadow: 0px 4px 8px 0px rgba(22, 19, 80, 0.28);

	&:hover {
		transform: translateY(-4px);
		.rank-card-cover-hover__overlay {
			opacity: 1;
		}
	}

	&__cover {
		width: 100%;
		height: 100%;
		object-fit: cover;
		transition: transform 0.3s ease;
	}

	&__overlay {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		opacity: 0;
		transition: opacity 0.3s ease;
		background: linear-gradient(180deg, rgba(22, 19, 80, 0.9) 0%, rgba(22, 19, 80, 0.3) 100%);
		backdrop-filter: blur(4px);
		color: #fff;
	}

	&__overlay-content {
		display: flex;
		flex-direction: column;
		justify-content: center;
		padding: rem(20) rem(16);
		height: 100%;
		box-sizing: border-box;
	}

	&__info {
		display: flex;
		flex-direction: column;
		gap: rem(12);
	}

	&__title {
		color: #fff;
		font-size: rem(14);
		font-style: normal;
		font-weight: 700;
		line-height: rem(16); /* 114.286% */
		display: -webkit-box;
		-webkit-line-clamp: 5;
		-webkit-box-orient: vertical;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	&__author {
		color: #fff;
		font-size: rem(12);
		font-style: normal;
		font-weight: 400;
		line-height: rem(12); /* 100% */
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	&__tags {
		display: flex;
		flex-direction: column;
		gap: rem(8);
		align-items: flex-start;
	}

	&__tag {
		display: inline-flex;
		align-items: center;
		gap: rem(8);
		padding: rem(6) rem(12);
		border-radius: rem(8);
		font-size: rem(13);
		font-weight: 700;
		border: 1px solid #fff;

		&--rating {
			img {
				width: rem(12);
				height: rem(12);
			}
		}
	}

	&__rank-badge {
		position: absolute;
		bottom: rem(0);
		left: rem(0);
		color: #fff;
		font-size: rem(12);
		font-weight: 700;
		z-index: 2;
		display: flex;
		width: rem(56);
		height: rem(24);
		justify-content: center;
		align-items: center;
		gap: rem(8);
		flex-shrink: 0;
		border-radius: rem(0) rem(12);
		border: rem(1) solid rgba(255, 255, 255, 0.1);
		background: #7f0026;
		box-shadow: rem(4) rem(-4) rem(8) rem(0) rgba(22, 19, 80, 0.24);
	}
}
.detect_deviece_mobile {
	.rank-card-cover-hover {
		position: relative;
		flex: none;
		width: rem(96);
		height: rem(144);
		border-radius: rem(12);
		overflow: hidden;
		cursor: pointer;
		transition: all 0.3s ease;
		flex-shrink: 0; /*  Prevent shrinking in a flex container */
		box-shadow: 0px 4px 8px 0px rgba(22, 19, 80, 0.24);
	}
}
</style> 